import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ModeTabsSlot } from "@/app/components/ModeTabs/ModeTabsPortal";
import { BackgroundStripes } from "@/ui/BackgroundStripes/BackgroundStripes";
import { useStartupParamsStore } from "@/store/startupParams";
import { RaffleDraw } from "@/apps/raffle/types";
import {
  RaffleHistoryBackIcon,
  RafflePrizeIcon,
} from "@/apps/raffle/pages/RafflePage/icons";
import { RafflePageLoader } from "@/apps/raffle/pages/RafflePage/RafflePageLoader";
import { formatMoney, RUBLE_SIGN } from "../RafflePage/utils";
import { useRaffleHistory } from "./useRaffleHistory";
import cls from "./styles.module.scss";

const formatDate = (date: string) =>
  new Intl.DateTimeFormat("ru-RU", {
    day: "numeric",
    month: "long",
  }).format(new Date(date));

const DrawCard = ({
  draw,
  onClick,
}: {
  draw: RaffleDraw;
  onClick: () => void;
}) => {
  const { t } = useTranslation();
  const result = draw.my_result;
  const hasPrize = Boolean(result?.is_winner) && result?.prize_amount !== null;

  return (
    <button type="button" className={cls.drawCard} onClick={onClick}>
      <div className={cls.drawMain}>
        <div className={cls.drawDates}>
          {formatDate(draw.period_start)} - {formatDate(draw.period_end)}
        </div>
      </div>
      {result && (
        <span
          className={[
            cls.drawPrizePill,
            hasPrize ? cls.drawPrizePillWon : "",
          ].join(" ")}
        >
          {hasPrize && <RafflePrizeIcon />}
          <span>
            {hasPrize
              ? `+${formatMoney(result.prize_amount ?? 0)} ${RUBLE_SIGN}`
              : t("raffle.history.noPrize")}
          </span>
        </span>
      )}
      {result && (
        <div className={cls.drawMeta}>
          <span>{t("raffle.history.orders", { count: result.rides })}</span>
        </div>
      )}
    </button>
  );
};

const RaffleHistoryShell = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <section className={cls.root}>
      <BackgroundStripes color="gray" className={cls.backgroundStripes} />
      <div className={cls.content}>
        <ModeTabsSlot className={cls.modeTabs} />
        <button
          type="button"
          className={cls.backButton}
          onClick={() => navigate("/apps/raffle")}
        >
          <RaffleHistoryBackIcon />
          <span>{t("raffle.history.button")}</span>
        </button>
        {children}
      </div>
    </section>
  );
};

export const RaffleHistoryPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const yandexId = useStartupParamsStore((state) => state.auth.yandexId);
  const { history, isLoading } = useRaffleHistory(yandexId);
  const finishedDraws = history?.draws.filter((draw) => draw.drawn_at);

  if (isLoading) {
    return (
      <RaffleHistoryShell>
        <RafflePageLoader />
      </RaffleHistoryShell>
    );
  }

  if (!finishedDraws?.length) {
    return (
      <RaffleHistoryShell>
        <p className={cls.emptyState}>{t("raffle.history.empty")}</p>
      </RaffleHistoryShell>
    );
  }

  return (
    <RaffleHistoryShell>
      {history && (
        <>
          <div className={cls.summary}>
            <div>
              <span className={cls.summaryLabel}>
                {t("raffle.history.totalWon")}
              </span>
              <strong className={cls.summaryWon}>
                {formatMoney(history.total_won)} {RUBLE_SIGN}
              </strong>
            </div>
            <div>
              <span className={cls.summaryLabel}>
                {t("raffle.history.participations")}
              </span>
              <strong className={cls.summaryParticipationCount}>
                {history.participation_count}
              </strong>
              <span className={cls.summaryParticipationText}>
                {t("raffle.history.participationCount", {
                  count: history.participation_count,
                })}
              </span>
            </div>
          </div>
          <div className={cls.drawsList}>
            {finishedDraws?.map((draw) => (
              <DrawCard
                key={draw.id}
                draw={draw}
                onClick={() => navigate(`/apps/raffle/${draw.id}`)}
              />
            ))}
          </div>
        </>
      )}
    </RaffleHistoryShell>
  );
};
